import React, { useState } from 'react';
import {
  Button,
  Modal,
  Alert,
  Input,
  Flex,
  Select,
  Radio,
  RadioChangeEvent,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useBranding } from '@src/theme/branding/useBranding';
import {
  useGetUserScenesQuery,
  useDeleteUserSceneMutation,
  useDeleteManyUserSceneAssetsMutation,
  UserScenesDocument,
  GetUserScenesDocument,
  UserScene,
  useDeleteManyLightPropsMutation,
  useGetMyTeamQuery,
  Brand,
  useFindFirstGraphicsSettingsLazyQuery,
  useDeleteOneGraphicsSettingsMutation,
  useDeleteOnePostProcessingSettingsMutation,
  useDeleteOneCloudConfigMutation,
  useDeleteOneFogConfigMutation,
  useCloneUserSceneMutation,
} from '@src/components/generated/graphql';
import { debounce } from 'lodash';
import { useSession } from 'next-auth/react';
import { S3_IMAGES_URL } from 'util/constants';
import { openNotification } from 'util/helpers';
import EditSceneModal from '@src/components/scene/editSceneModal';
import SceneThumbnail from '@src/components/scene/SceneThumbnail';
import styles from '../../../pages/user/studio.module.css';

enum SCENES_TYPE {
  TEAM_SCENES,
  MY_SCENES,
}

export const MyScenes = ({ isFromDrawer = false }) => {
  const { isZepeto } = useBranding();
  const [deleteLightProps] = useDeleteManyLightPropsMutation();
  const [deleteScene] = useDeleteUserSceneMutation();
  const [findGraphicsSettings] = useFindFirstGraphicsSettingsLazyQuery();
  const [deleteGraphicsSettings] = useDeleteOneGraphicsSettingsMutation();
  const [deletePostProcessingSettings] =
    useDeleteOnePostProcessingSettingsMutation();
  const [deleteCloudConfig] = useDeleteOneCloudConfigMutation();
  const [deleteFogConfig] = useDeleteOneFogConfigMutation();
  const [cloneScene, { loading: cloneLoading }] = useCloneUserSceneMutation();
  const { data: session } = useSession();
  const [selectedScenesType, setSelectedScenesType] = useState<SCENES_TYPE>(
    SCENES_TYPE.MY_SCENES
  );
  const [deleteValidation, setDeleteValidation] = useState<boolean>(true);
  const [deleteAssets] = useDeleteManyUserSceneAssetsMutation();
  const [modalScene, setModalScene] = useState<UserScene | null>();
  const [isModalDeleteSceneOpen, setIsModalDeleteSceneOpen] =
    useState<boolean>(false);
  const [isModalEditSceneOpen, setIsModalEditSceneOpen] =
    useState<boolean>(false);
  const [nameToSearch, setNameToSearch] = useState<string>('');
  const [sortType, setSortType] = useState<'date' | 'name'>('date');

  const debouncedSetNameToSearch = debounce((name: string) => {
    setNameToSearch(name);
  }, 1000);

  const handleSearchChange = (input) => {
    const value = input.target.value;
    if (value.length <= 1) {
      debouncedSetNameToSearch.cancel();
      setNameToSearch('');
      return;
    }
    debouncedSetNameToSearch(value);
  };

  const { data: myTeamData } = useGetMyTeamQuery();
  const team = myTeamData?.getMyTeam.team;

  const {
    data: userSceneData,
    loading: isLoading,
    error: isError,
  } = useGetUserScenesQuery({
    variables: {
      where: {
        userId: session?.user.id ?? '',
        branding: isZepeto ? Brand.Zepeto : Brand.Studio,
        search: nameToSearch || undefined,
        orderBy: sortType,
        teamId:
          selectedScenesType === SCENES_TYPE.TEAM_SCENES && team
            ? team.id
            : undefined,
      },
    },
  });

  const userScenes = userSceneData?.getUserScenes.scenes || [];

  async function deleteUserScene(sceneId) {
    try {
      const graphicsSettingsResult = await findGraphicsSettings({
        variables: {
          where: {
            UserScene: {
              some: {
                id: {
                  equals: sceneId,
                },
              },
            },
          },
        },
      });

      const graphicsSettings =
        graphicsSettingsResult.data?.findFirstGraphicsSettings;

      if (graphicsSettings?.postProcessingSettingsId) {
        await deletePostProcessingSettings({
          variables: {
            where: {
              id: graphicsSettings.postProcessingSettingsId,
            },
          },
        });
      }

      if (graphicsSettings?.cloudConfigId) {
        await deleteCloudConfig({
          variables: {
            where: {
              id: graphicsSettings.cloudConfigId,
            },
          },
        });
      }

      if (graphicsSettings?.fogConfigId) {
        await deleteFogConfig({
          variables: {
            where: {
              id: graphicsSettings.fogConfigId,
            },
          },
        });
      }

      if (graphicsSettings?.id) {
        await deleteGraphicsSettings({
          variables: {
            where: {
              id: graphicsSettings.id,
            },
          },
        });
      }

      await deleteLightProps({
        variables: {
          where: {
            assets: {
              every: {
                userSceneId: {
                  equals: sceneId,
                },
              },
            },
          },
        },
      });

      await deleteAssets({
        variables: {
          where: {
            userSceneId: {
              equals: sceneId,
            },
          },
        },
      });

      await deleteScene({
        variables: {
          where: {
            id: sceneId,
          },
        },
        refetchQueries: [UserScenesDocument, GetUserScenesDocument],
      });
    } catch (error) {
      console.error('Error deleting scene and related data:', error);
      throw error;
    }
  }

  const handleDeleteOk = async () => {
    await deleteUserScene(modalScene?.id);
    resetModalDelete();
  };

  const handleDeleteCancel = () => {
    resetModalDelete();
  };

  const resetModalDelete = () => {
    setDeleteValidation(true);
    setModalScene(undefined);
    setIsModalDeleteSceneOpen(false);
  };

  const deleteInputValidation = (e) => {
    if (e.target.value === 'delete') {
      setDeleteValidation(false);
    } else {
      setDeleteValidation(true);
    }
  };

  const handleCloneScene = async (sceneId: string) => {
    try {
      const result = await cloneScene({
        variables: {
          where: {
            sceneId,
            userId: session?.user.id ?? '',
          },
        },
        refetchQueries: [UserScenesDocument, GetUserScenesDocument],
      });

      if (result.data?.cloneUserScene?.status) {
        openNotification(
          'Scene cloned successfully!',
          '',
          'top',
          2.0,
          'success'
        );
      } else {
        openNotification(
          result.data?.cloneUserScene?.message || 'Failed to clone scene',
          '',
          'top',
          2.0,
          'error'
        );
      }
    } catch (error) {
      console.error('Error cloning scene:', error);
      openNotification('Failed to clone scene', '', 'top', 2.0, 'error');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingRoot}>
        <div>
          <img
            src={`${S3_IMAGES_URL}/assets/logos/ozoneStudioLogo.png`}
            className={styles.spin}
            alt='spin'
          />
          <h3 className={styles.loadingText}>Loading</h3>
        </div>
      </div>
    );
  }

  if (isError) {
    return <div>Error loading scenes.</div>;
  }

  return (
    <>
      <Modal
        title='Delete Scene'
        centered
        zIndex={1002}
        open={isModalDeleteSceneOpen}
        footer={null}
        destroyOnClose
        onCancel={handleDeleteCancel}
      >
        <Alert
          type='warning'
          message={
            <>
              <span>Are you sure you want to permanently delete scene?</span>
              <ul>
                <li>Scene will be permanently deleted</li>
                <li>Deleting scene can't be undone.</li>
              </ul>
            </>
          }
          showIcon
        />
        <div className={styles.alertInputWrapper}>
          <p>
            To confirm deletion, type{' '}
            <span className={styles.fontBold}>delete</span>.
          </p>
          <Input
            className={styles.input}
            onChange={deleteInputValidation}
            placeholder={'delete'}
            data-cy='delete-scene-input'
          />
        </div>
        <div className={styles.deleteButtonsWrapper}>
          <Button
            type='primary'
            onClick={handleDeleteOk}
            disabled={deleteValidation}
            className={styles.deleteButton}
            data-cy='delete-scene-submit-button'
          >
            <span style={{ color: '#fff' }}>Delete</span>
          </Button>
          <Button
            type='primary'
            onClick={handleDeleteCancel}
            className={styles.cancelButton}
          >
            <span style={{ color: '#fff' }}>Cancel</span>
          </Button>
        </div>
      </Modal>
      <EditSceneModal
        isModalEditSceneOpen={isModalEditSceneOpen}
        setIsModalEditSceneOpen={setIsModalEditSceneOpen}
        userScene={modalScene}
        userId={session?.user.id ?? ''}
        selectedScenesType={selectedScenesType}
        teamId={team?.id}
      />
      <div className={styles.headingWrapper}>
        <h1 className={styles.heading}>My scenes</h1>
      </div>
      {team && (
        <Radio.Group
          options={[
            { label: 'My Scenes', value: SCENES_TYPE.MY_SCENES },
            { label: 'Team Scenes', value: SCENES_TYPE.TEAM_SCENES },
          ]}
          onChange={({ target: { value } }: RadioChangeEvent) =>
            setSelectedScenesType(value)
          }
          value={selectedScenesType}
          optionType='button'
          buttonStyle='solid'
          className={styles.sceneTypeSwitcher}
        />
      )}
      <div className={styles.sortAndSearch}>
        <Input
          style={{ width: '100%' }}
          placeholder='Scene name'
          className={styles.sceneSearch}
          onChange={handleSearchChange}
          prefix={<SearchOutlined className={styles.iconSearch} />}
        />
        <Select
          className={styles.sortScene}
          defaultValue='date'
          onChange={setSortType}
          options={[
            { value: 'date', label: 'Date created' },
            { value: 'name', label: 'A-z' },
          ]}
        />
      </div>
      <div
        className={
          userScenes.length > 0
            ? styles.containerStudioScenes
            : styles.emptyWrapper
        }
      >
        <div
          className={styles.addSceneCard}
          onClick={() => {
            setModalScene(null);
            setIsModalEditSceneOpen(true);
          }}
        >
          <div className={styles.plusWrapper}>
            <div className={styles.addSceneCardPlus}>+</div>
          </div>
          <span className={styles.createSceneText}>Create new scene</span>
        </div>
        {userScenes.length > 0 ? (
          <>
            {userScenes.map((item) => {
              return (
                <SceneThumbnail
                  isFromDrawer={isFromDrawer}
                  sceneData={item}
                  key={`Card-${item.id}`}
                  setIsModalDeleteSceneOpen={setIsModalDeleteSceneOpen}
                  setIsModalEditSceneOpen={setIsModalEditSceneOpen}
                  setModalScene={setModalScene}
                  onCloneScene={handleCloneScene}
                />
              );
            })}
          </>
        ) : (
          <Flex vertical justify='center' align='center'>
            <img src='/empty-studio-icon.svg' alt='empty' width={130} />
            <p className={styles.emptyHint}>
              You don't have any scenes created
            </p>
          </Flex>
        )}
      </div>
    </>
  );
};

export default MyScenes;
